import SimpleModal from 'components/SimpleModal';
import { Box, Typography } from '@mui/material';
import { Realm } from 'types';
import useSandboxes, { SandboxIsoStateIds } from 'SandboxModule/hooks/useSandboxes';
import { checkIsSandboxDestroyable } from 'utils';
import { useForceCleanUpSubscriptionMutation } from 'RealmModule/query/hooks/useForceCleanUpSubscriptionMutation';

type ForceCleanUpSubscriptionModalProps = {
  onClose: VoidFunction;
  realmId: Realm['realm_id'];
  onForceCleanUpSuccess?: VoidFunction;
};

export const ForceCleanUpSubscriptionModal = ({
  onClose,
  realmId,
  onForceCleanUpSuccess,
}: ForceCleanUpSubscriptionModalProps) => {
  const { getSandbox, fetchSandboxes } = useSandboxes();
  const realm = getSandbox(realmId);
  // Never coerce a missing tenant to '': buildUrlHeaders treats the empty string
  // as "not supplied" and substitutes the URL-context or auth-default tenant, which
  // would run this non-recoverable DELETE under the wrong tenant scope.
  const tenant = realm?.tenant;
  // The menu re-checks the status at click time, but this modal then waits on a
  // human. Re-check against the live realm list on every render so a subscription
  // whose teardown finishes while the dialog is open cannot be force cleaned up.
  const canForceCleanUp = !!realmId && !!tenant && !!realm && checkIsSandboxDestroyable(realm);

  const { forceCleanUpSubscription, isForceCleaningUpSubscription } =
    useForceCleanUpSubscriptionMutation({
      onSuccess: () => {
        fetchSandboxes(SandboxIsoStateIds.SandboxTable);
        onForceCleanUpSuccess?.();
      },
    });

  const onConfirm = () => {
    if (!canForceCleanUp || !tenant) {
      return;
    }

    forceCleanUpSubscription({ realmId, tenant });
    onClose();
  };

  return (
    <SimpleModal
      open
      onClose={onClose}
      onConfirm={onConfirm}
      headerText="Force Clean Up Subscription"
      confirmButtonText="Force Clean Up"
      onConfirmButtonClassNames="red-warning-button"
      disableConfirmButton={isForceCleaningUpSubscription || !canForceCleanUp}
      Body={
        <div>
          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
            {realmId || 'N/A'} • Tenant: {tenant || 'N/A'} • Team Name: {realm?.team_name || 'N/A'}
          </Typography>
          <Box
            sx={{
              margin: '1rem 0',
            }}
          >
            Destroying a subscription realm leaves its databases and secrets in place. This drops
            them immediately.
          </Box>
          <Box
            sx={{
              margin: '1rem 0',
            }}
          >
            <b>
              Databases and secrets are dropped right away and are not recoverable — there is no
              retention window.
            </b>
          </Box>
        </div>
      }
    />
  );
};
